import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import { useAuth } from '../../context/AuthContext';
import {
  supabase,
  saveFullConfiguration,
  createCaregiverWithAuthId,
  linkSessionToConfiguration,
  type WizardData,
} from '../../lib/supabase';
import { REFERRAL_SOURCE_OPTIONS } from '../../constants/referral-sources';
import { uploadPendingContactPhotos, SIGNUP_PHOTO_NOTICE_MS } from '../../lib/pendingContactPhotos';
import { contactPhotoText } from '../../components/features/contactPhoto/contactPhotoText';

interface DeploymentProps {
  onComplete: () => void;
}

const PLAN_PRICES: Record<string, number> = { plus: 10.99 };

function generateActivationCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

type Phase = 'form' | 'saving' | 'redirecting' | 'error';
type StepId = 1 | 2 | 3 | 4;

const Deployment: FC<DeploymentProps> = ({ onComplete: _onComplete }) => {
  const { t, i18n } = useTranslation();
  // CASL: the consent text must carry a reachable Privacy Policy link. Legal
  // slugs are language-specific and are STATIC files served by Netlify
  // rewrites, not React routes — same resolution as components/landing/Footer.tsx.
  const privacyUrl = i18n.language?.startsWith('fr')
    ? '/fr/politique-de-confidentialite'
    : '/privacy-policy';
  const config = useConfiguration();
  const { user, signUp } = useAuth();
  const { sessionId, clearWizardSession, setActivationCode } = config;

  const [phase, setPhase] = useState<Phase>('form');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  // CASL: SMS opt-in must be explicit and separate from ToS. Default OFF;
  // caregiver receives zero SMS unless they affirmatively check this.
  const [smsOptIn, setSmsOptIn] = useState(false);
  // Ticket 2 — optional "How did you hear about AidFone?" attribution.
  const [referralSource, setReferralSource] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [progressLabel, setProgressLabel] = useState<string>(t('deploymentSignup.creatingAccount'));
  const [currentStep, setCurrentStep] = useState<StepId>(1);
  const [photosNotSaved, setPhotosNotSaved] = useState(false);

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPhotosNotSaved(false);
    setPhase('saving');

    try {
      // 1. Sign up (or reuse existing session if already authed — edge case for retries)
      let authUserId: string;
      if (user) {
        authUserId = user.id;
      } else {
        setCurrentStep(1);
        setProgressLabel(t('deploymentSignup.creatingAccount'));
        const { error: signUpError } = await signUp(email, password, firstName);
        if (signUpError) throw signUpError;

        // Email confirmation is disabled, so the session should be active immediately.
        const { data: userData, error: getUserError } = await supabase.auth.getUser();
        if (getUserError) throw getUserError;
        if (!userData.user) throw new Error('Sign up succeeded but no user was returned');
        authUserId = userData.user.id;
      }

      // 2. Create the caregivers row tied to the auth user id
      setCurrentStep(2);
      setProgressLabel(t('deploymentSignup.settingUpProfile'));
      await createCaregiverWithAuthId({
        authUserId,
        email: email || user?.email || '',
        fullName: firstName || (user?.user_metadata as { full_name?: string } | undefined)?.full_name || '',
        // Phone is only saved if the caregiver provided one AND opted in for
        // SMS. If they unchecked SMS opt-in we leave phone null — no value in
        // storing a number we can't use.
        phone: smsOptIn && phone ? phone : null,
        referralSource: referralSource || null,
      });

      // Contact photos cropped at the contacts step were kept on this device:
      // nobody may upload before signing in. Upload them now the account
      // exists. A photo that fails is dropped and said on screen; it never
      // blocks the account or the payment.
      const photos = await uploadPendingContactPhotos(config.contacts);
      if (photos.failed > 0) {
        setPhotosNotSaved(true);
        await new Promise((resolve) => setTimeout(resolve, SIGNUP_PHOTO_NOTICE_MS));
      }

      // 3. Build wizard data and save the senior config
      setCurrentStep(3);
      setProgressLabel(t('deploymentSignup.savingProfile'));
      const activationCode = generateActivationCode();
      const answersRecord: Record<number, number> = {};
      config.assessmentAnswers.forEach((a) => {
        answersRecord[a.questionId] = a.value;
      });

      const wizardData: WizardData = {
        caregiverId: authUserId,
        seniorName: config.userDetails.fullName || 'New Senior',
        seniorSexAtBirth: config.userDetails.sexAtBirth || undefined,
        profileType: config.selectedProfile as 'cognitive' | 'physical' | 'visual',
        severity: config.severityLevel || 'moderate',
        assessmentScore: config.severityScore || 2.5,
        assessmentAnswers: answersRecord,
        planTier: config.selectedPlan || 'plus',
        planPrice: PLAN_PRICES[config.selectedPlan || 'plus'],
        layoutId: config.selectedLayoutId || 'ma_1',
        contacts: photos.contacts.map((c) => ({
          name: c.name,
          phone: c.phone,
          relationship: c.relationship,
          photoUrl: c.photoUrl,
          isEmergency: c.isEmergency,
          aliases: c.aliases || [],
        })),
        // REMOVED: geoFenceEnabled / Lat / Lng / Radius / Schedule.
        //
        // Geofencing is CUT from v1 (Feature 03 spec) and step 5 no longer
        // collects a zone. Dropping these five fields stops the INSERT from
        // touching configurations.geo_fence_enabled / _address / _radius /
        // _alert_on_exit — the columns remain in both Supabase projects and
        // now simply take their own defaults (false / NULL / 500 / true).
        //
        // Worth knowing on restore: _address never held an address. The write
        // path packed the map coordinates into it as `${lat},${lng}`
        // (src/lib/supabase.ts), and the schedule had no column at all and was
        // silently dropped. Fix both before this comes back in Wave 1.5.
        healthMedicationReminders: config.healthSettings?.medicationReminders,
        healthDailyCheckin: config.healthSettings?.dailyCheckIn,
        healthCheckinTime: config.healthSettings?.checkInTime,
        activationCode,
      };

      const result = await saveFullConfiguration(wizardData);
      setActivationCode(activationCode);

      // CASL: if the caregiver opted in to SMS during signup, write the
      // immutable consent receipt via the service-role Edge Function.
      // Fire-and-forget — a failure here shouldn't block the user from
      // reaching Stripe checkout. Worst case: SMS won't fire until they
      // re-opt-in from the dashboard, which they can do later.
      if (smsOptIn && result.senior?.id) {
        supabase.functions.invoke('log-consent', {
          body: {
            seniorId: result.senior.id,
            eventType: 'caregiver_request',
            telemetryScope: { sms: true },
            caregiverId: authUserId,
          },
        }).catch((err) => {
          console.warn('[CASL] log-consent insert failed (non-blocking):', err);
        });
      }

      // 4. Link wizard session
      //
      // REMOVED: the parallel write of the safe zone to Firebase RTDB at
      // `devices/{seniorId}/geofence` ({ enabled, homeLat, homeLng,
      // radiusMeters, schedule, updatedAt }).
      //
      // Geofencing is CUT from v1 (Feature 03 spec) and step 5 no longer
      // collects a zone. The write was already failing: the RTDB rules
      // deployed 2026-08-16 deny that path, and the rejection was swallowed by
      // a .catch(console.warn) — so signup carried on to Stripe looking
      // successful while the zone went nowhere. Removing it deletes a silent
      // failure, not a working feature.
      //
      // Restore in Wave 1.5 only after the RTDB rules permit the path again
      // and the Android release flag is on; GeofencingService.kt reads exactly
      // this shape from exactly this path.
      const parallelTasks: Promise<void>[] = [];
      if (sessionId && result.senior.id && result.configuration.id) {
        parallelTasks.push(
          linkSessionToConfiguration(sessionId, result.senior.id, result.configuration.id)
            .then(() => clearWizardSession())
            .catch((err) => console.warn('[WizardSession] Failed to link:', err))
        );
      }
      await Promise.all(parallelTasks);

      // 5. Redirect to Stripe checkout
      setCurrentStep(4);
      setProgressLabel(t('deploymentSignup.redirectingPayment'));
      setPhase('redirecting');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seniorId: result.senior.id,
          planTier: 'plus',
          caregiverEmail: email || user?.email || '',
          caregiverFirstName: firstName,
          seniorName: config.userDetails.fullName || '',
          language: navigator.language?.split('-')[0] || 'en',
        }),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');
      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error('[Deployment] Error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setPhase('error');
    }
  };

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <span className="material-symbols-outlined text-4xl text-red-600 dark:text-red-400">error</span>
          </div>
          <h2 className="text-2xl font-display font-bold text-brand-text dark:text-white mb-2">
            {t('deploymentSignup.errorTitle')}
          </h2>
          <p className="text-sm text-brand-text-muted dark:text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => {
              setPhase('form');
              setError(null);
            }}
            className="px-6 py-3 bg-brand-primary text-white rounded-xl font-bold hover:bg-brand-primary-dark transition-colors"
          >
            {t('deploymentSignup.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saving' || phase === 'redirecting') {
    const steps: { id: StepId; key: string }[] = [
      { id: 1, key: 'stepAccount' },
      { id: 2, key: 'stepProfile' },
      { id: 3, key: 'stepConfiguration' },
      { id: 4, key: 'stepPayment' },
    ];

    return (
      <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body flex items-center justify-center px-4 animate-fadeIn">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700 p-8 shadow-sm">
            <h2 className="text-xl font-display font-bold text-brand-text dark:text-white mb-1">
              {progressLabel}
            </h2>
            <p className="text-sm text-brand-text-muted dark:text-slate-400 mb-6">
              {t('deploymentSignup.quickMoment')}
            </p>

            <ol className="flex flex-col gap-3" aria-label={t('deploymentSignup.progressLabel')}>
              {steps.map(({ id, key }) => {
                const status =
                  id < currentStep ? 'done' : id === currentStep ? 'active' : 'pending';
                return (
                  <li
                    key={id}
                    className="flex items-center gap-3 transition-opacity duration-200"
                    aria-current={status === 'active' ? 'step' : undefined}
                  >
                    <span
                      className={
                        'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ' +
                        (status === 'done'
                          ? 'bg-brand-success text-white'
                          : status === 'active'
                            ? 'bg-brand-primary text-white'
                            : 'bg-brand-bg-warm dark:bg-slate-700 text-brand-text-muted dark:text-slate-400')
                      }
                      aria-hidden="true"
                    >
                      {status === 'done' ? (
                        <span className="material-symbols-outlined text-base">check</span>
                      ) : status === 'active' ? (
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        id
                      )}
                    </span>
                    <span
                      className={
                        'text-sm transition-colors ' +
                        (status === 'done'
                          ? 'text-brand-text dark:text-white font-medium'
                          : status === 'active'
                            ? 'text-brand-text dark:text-white font-semibold'
                            : 'text-brand-text-muted dark:text-slate-400')
                      }
                    >
                      {t(`deploymentSignup.${key}`)}
                    </span>
                  </li>
                );
              })}
            </ol>

            {photosNotSaved && (
              <p role="alert" className="mt-6 text-sm text-red-700 dark:text-red-400">
                {contactPhotoText(i18n.language, 'signupPhotosNotSaved')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // phase === 'form'
  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700 p-8 shadow-sm">
          <h2 className="text-2xl font-display font-bold text-brand-text dark:text-white mb-1">
            {t('deploymentSignup.title')}
          </h2>
          <p className="text-sm text-brand-text-muted dark:text-slate-400 mb-6">
            {t('deploymentSignup.subtitle')}
          </p>

          <form onSubmit={handleCreateAccount} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text dark:text-white">
                {t('deploymentSignup.firstName')}
              </span>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text dark:text-white">
                {t('deploymentSignup.email')}
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text dark:text-white">
                {t('deploymentSignup.password')}
              </span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>

            {/* Optional phone — only required if SMS opt-in is checked. E.164 format. */}
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text dark:text-white">
                {t('deploymentSignup.phone')}
                {!smsOptIn && (
                  <span className="text-xs font-normal text-brand-text-muted dark:text-slate-400 ml-2">
                    {t('deploymentSignup.phoneOptional')}
                  </span>
                )}
              </span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                pattern="^\+[1-9]\d{10,14}$"
                placeholder="+15145551234"
                required={smsOptIn}
                value={phone}
                onChange={(e) => setPhone(e.target.value.trim())}
                className="px-4 py-3 rounded-xl border border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
              <span className="text-xs text-brand-text-muted dark:text-slate-400">
                {t('deploymentSignup.phoneHint')}
              </span>
            </label>

            {/* CASL opt-in — explicit, separate-from-ToS checkbox.
                See pdf/Feature 01 - SMS & Twilio.pdf §CASL compliance. */}
            <label className="flex items-start gap-3 mt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-brand-border dark:border-slate-600 text-brand-accent focus:ring-2 focus:ring-brand-accent/30"
              />
              <span className="text-sm text-brand-text dark:text-white leading-snug">
                {t('deploymentSignup.smsOptIn')}{' '}
                {/* Plain <a>, not react-router <Link>: the legal pages are static
                    files behind _redirects rewrites, and a <Link> to one renders
                    NotFound (the 2026-07 privacy-policy 404). Opens in a new tab
                    because this wizard keeps no draft — navigating away in-tab
                    would discard everything typed so far.

                    Consent must not be togglable by reading the policy. The
                    guarantee is the HTML spec's: a <label> does not run its
                    activation behaviour when the click lands on interactive
                    content, and <a href> is interactive content — so the
                    checkbox does not flip. Verified in-browser, both languages.
                    stopPropagation is defence-in-depth for that, not the
                    mechanism: it stops React's synthetic bubble, which is a
                    different path from native label activation. */}
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="underline text-brand-primary hover:text-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 rounded-sm"
                >
                  {t('deploymentSignup.smsOptInPrivacyLink')}
                </a>
              </span>
            </label>

            {/* Ticket 2 — optional attribution survey. Stored on the caregiver
                record (referral_source) for the launch dashboard query. */}
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-brand-text dark:text-white">
                {t('deploymentSignup.referralLabel')}
              </span>
              <select
                value={referralSource}
                onChange={(e) => setReferralSource(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              >
                <option value="">{t('deploymentSignup.referralPlaceholder')}</option>
                {REFERRAL_SOURCE_OPTIONS.map((key) => (
                  <option key={key} value={key}>
                    {t(`deploymentSignup.referralOptions.${key}`)}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="submit"
              disabled={
                !firstName || !email || password.length < 8 ||
                (smsOptIn && !/^\+[1-9]\d{10,14}$/.test(phone)) ||
                phase !== 'form'
              }
              className="py-4 bg-brand-accent text-white rounded-xl font-bold text-lg hover:bg-[#c96a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_14px_rgba(224,123,76,0.35)]"
            >
              {t('deploymentSignup.startTrial')}
            </button>
            <p className="text-xs text-brand-text-muted dark:text-slate-400 text-center">
              {t('deploymentSignup.trialFooter')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Deployment;
